import { Injectable, inject, signal } from '@angular/core';
import { TrackingApiService } from './tracking-api.service';
import { TrackingAdminTokenService } from './tracking-admin-token.service';
import type { TrackingWsEnvelope } from '../tracking-demo.models';

/**
 * Admin dashboard WebSocket (`…/lead_gen/ws/pipeline/live-call/tracking`, the
 * Apache-tunneled alias of `…/live-call/api/tracking/ws` — see TrackingApiService.wsUrl).
 *
 * Modeled on the credee-demo RoomChannel: first-frame auth handshake, exponential
 * backoff reconnect, keep-alive ping, and an `onReconnected` hook so the owner
 * re-syncs over REST (the socket alone is never the source of truth). It carries the
 * dashboard-wide live events (session_*, participant_*, ip_*, metrics). The old
 * covert per-session watch feed is retired — the admin joins the real room now.
 */
export interface TrackingSocketOptions {
  onEvent: (env: TrackingWsEnvelope) => void;
  onReconnected?: () => void;
  onUnauthorized?: () => void;
}

export type TrackingSocketState = 'connecting' | 'live' | 'reconnecting' | 'offline' | 'unauthorized';

@Injectable()
export class TrackingSocketService {
  private readonly api = inject(TrackingApiService);
  private readonly tokens = inject(TrackingAdminTokenService);

  private static readonly BACKOFF_BASE_MS = 500;
  private static readonly BACKOFF_MAX_MS = 8000;
  private static readonly MAX_RETRIES = 10;
  private static readonly PING_MS = 25_000;
  private static readonly UNAUTH_CODES = [4400, 4401, 4403];

  readonly state = signal<TrackingSocketState>('offline');

  private ws: WebSocket | null = null;
  private opts: TrackingSocketOptions | null = null;
  private retries = 0;
  private everReady = false;
  private stopped = true;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;

  connect(opts: TrackingSocketOptions): void {
    this.close();
    this.opts = opts;
    this.stopped = false;
    this.retries = 0;
    this.open();
  }

  close(): void {
    this.stopped = true;
    if (this.pingTimer) { clearInterval(this.pingTimer); this.pingTimer = null; }
    if (this.retryTimer) { clearTimeout(this.retryTimer); this.retryTimer = null; }
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      ws.onopen = ws.onmessage = ws.onclose = ws.onerror = null;
      try { ws.close(); } catch { /* already closed */ }
    }
    this.everReady = false;
    this.state.set('offline');
  }

  private open(): void {
    if (!this.opts || this.stopped) return;
    this.state.set(this.everReady ? 'reconnecting' : 'connecting');
    let ws: WebSocket;
    try {
      ws = new WebSocket(this.api.wsUrl());
    } catch {
      this.scheduleRetry();
      return;
    }
    this.ws = ws;

    ws.onopen = () => {
      // First frame: authenticate with the admin token; adminName/adminId are
      // attribution only (URI-encoded like the REST headers, unquoted server-side).
      try {
        const name = this.api.adminName();
        const id = this.api.adminId();
        ws.send(JSON.stringify({
          type: 'auth', token: this.tokens.token() || '',
          adminName: name ? encodeURIComponent(name) : undefined,
          adminId: id ? encodeURIComponent(id) : undefined,
        }));
      } catch { /* onclose recovers */ }
    };

    ws.onmessage = (ev: MessageEvent) => {
      let env: TrackingWsEnvelope | null = null;
      try { env = JSON.parse(String(ev.data)) as TrackingWsEnvelope; } catch { return; }
      if (!env?.type) return;
      if (env.type === 'ready') {
        const reconnected = this.everReady;
        this.everReady = true;
        this.retries = 0;
        this.state.set('live');
        this.startPing();
        if (reconnected) this.opts?.onReconnected?.();
        return;
      }
      if (env.type === 'pong') return;
      this.opts?.onEvent(env);
    };

    ws.onclose = (evt: CloseEvent) => {
      if (this.stopped) return;
      if (this.pingTimer) { clearInterval(this.pingTimer); this.pingTimer = null; }
      if (TrackingSocketService.UNAUTH_CODES.includes(evt.code)) {
        this.stopped = true;
        this.state.set('unauthorized');
        this.tokens.invalidate();
        this.opts?.onUnauthorized?.();
        return;
      }
      this.scheduleRetry();
    };

    ws.onerror = () => { /* onclose follows and owns the retry */ };
  }

  private scheduleRetry(): void {
    if (!this.opts || this.stopped) return;
    this.state.set('reconnecting');
    if (this.retries >= TrackingSocketService.MAX_RETRIES) {
      this.state.set('offline');
      return;
    }
    const delay = Math.min(
      TrackingSocketService.BACKOFF_BASE_MS * 2 ** this.retries,
      TrackingSocketService.BACKOFF_MAX_MS,
    );
    this.retries += 1;
    this.retryTimer = setTimeout(() => this.open(), delay);
  }

  private startPing(): void {
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.pingTimer = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        try { this.ws.send('{"type":"ping"}'); } catch { /* close handler recovers */ }
      }
    }, TrackingSocketService.PING_MS);
  }
}
